import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { trackUpgrade } from "../lib/analytics";
import { useAuth } from "../contexts/AuthContext";
import { PasswordInput } from "../components/auth/PasswordInput";

interface BalanceResponse {
  minutes_remaining: number;
  free_minutes_used: number;
  purchased_minutes_used: number;
}

export default function BillingSuccessPage() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [minutes, setMinutes] = useState<number | null>(null);

  // Inline signup form state
  const [_sessionEmail, setSessionEmail] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupError, setSignupError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Track upgrade event on page load
  useEffect(() => {
    trackUpgrade();
  }, []);

  // Fetch session email for guest users
  useEffect(() => {
    if (user || !sessionId) return;
    apiFetch<{ email?: string }>(`/billing/session-email?session_id=${sessionId}`)
      .then((data) => {
        if (data.email) {
          setSessionEmail(data.email);
          setEmail(data.email);
        }
      })
      .catch(() => {});
  }, [user, sessionId]);

  // Fetch balance for logged-in users
  useEffect(() => {
    if (!user) return;
    apiFetch<BalanceResponse>("/billing/balance")
      .then((data) => setMinutes(data.minutes_remaining))
      .catch(() => {});
  }, [user, sessionId]);

  async function handleSignup(e: FormEvent) {
    e.preventDefault();
    setSignupError("");

    if (password.length < 6) {
      setSignupError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setSignupError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await signup(email, password, "");
      navigate("/");
    } catch (err) {
      setSignupError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-white px-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-8 w-8 text-green-600"
          >
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900">
          Payment successful!
        </h1>

        {user ? (
          <>
            <p className="mt-2 text-gray-500">
              Your minutes have been added to your account.
            </p>
            {minutes !== null && (
              <p className="mt-1 text-sm font-medium text-green-600">
                Balance: {minutes} minutes
              </p>
            )}
            <div className="mt-8">
              <Link
                to="/"
                className="inline-block rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Go to home
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-gray-500">
              Your transcription minutes are ready.
            </p>

            <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-5 py-5 text-left">
              <p className="text-sm font-medium text-blue-900">
                Create an account with your email to access your minutes.
              </p>

              <form onSubmit={handleSignup} className="mt-4 space-y-3">
                {signupError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                    {signupError}
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={setPassword}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Confirm password
                  </label>
                  <PasswordInput
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    required
                    className={inputClass}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? "Creating account..." : "Create account"}
                </button>
              </form>
            </div>

            <div className="mt-6">
              <Link
                to="/"
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Skip for now
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
